import { useState, useEffect, useRef } from 'react';
import { countries, defaultCountry, getCountryByCode } from '../../data/countries.js';
import CountryFlag from './CountryFlag.jsx';
import { extractLocalDigits, sanitizeLocalPhoneInput } from '../../utils/phone-input.util.js';
import './CountryPhoneInput.css';

const LOCAL_PLACEHOLDER = {
  SN: '77 123 45 67 (9 chiffres)',
  FR: '6 12 34 56 78',
  US: '415 555 1234',
  CA: '416 555 1234',
};

function CountryPhoneInput({
  value,
  onChange,
  onCountryChange,
  errors = {},
  disabled = false,
  id,
  senegalOnly = false,
  countryCode,
  lockCountryPrefix = false,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}) {
  const senegal = getCountryByCode('SN') || defaultCountry;
  const prefixLocked = senegalOnly || (lockCountryPrefix && Boolean(countryCode));
  const [selectedCountry, setSelectedCountry] = useState(senegalOnly ? senegal : defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [inputHint, setInputHint] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const resolvedCountry = prefixLocked
    ? (senegalOnly ? senegal : getCountryByCode(countryCode))
    : selectedCountry;

  useEffect(() => {
    const countryForValue = prefixLocked
      ? (senegalOnly ? senegal : getCountryByCode(countryCode))
      : null;

    if (prefixLocked && countryForValue) {
      setSelectedCountry(countryForValue);
      const numberPart = extractLocalDigits(value || '', countryForValue);
      setPhoneNumber(numberPart);
      return;
    }

    if (value) {
      const matchingCountry = countries
        .filter((c) => value.startsWith(c.phoneCode))
        .sort((a, b) => b.phoneCode.length - a.phoneCode.length)
        .find((c) => !countryCode || c.code === countryCode)
        || countries
          .filter((c) => value.startsWith(c.phoneCode))
          .sort((a, b) => b.phoneCode.length - a.phoneCode.length)[0];

      if (matchingCountry) {
        setSelectedCountry(matchingCountry);
        setPhoneNumber(extractLocalDigits(value, matchingCountry));
      } else if (countries.some((c) => c.phoneCode === value)) {
        const country = countries.find((c) => c.phoneCode === value);
        if (country) {
          setSelectedCountry(country);
          setPhoneNumber('');
        }
      }
    } else {
      setSelectedCountry(countryCode ? getCountryByCode(countryCode) : defaultCountry);
      setPhoneNumber('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, senegalOnly, countryCode, lockCountryPrefix]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const isFirstRender = useRef(true);
  const onCountryChangeRef = useRef(onCountryChange);
  useEffect(() => {
    onCountryChangeRef.current = onCountryChange;
  });
  useEffect(() => {
    if (prefixLocked) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onCountryChangeRef.current?.(selectedCountry);
  }, [selectedCountry, prefixLocked]);

  const updateFullNumber = (country, number) => {
    const cleanedNumber = number.replace(/[^\d]/g, '').trim();
    const fullNumber = cleanedNumber
      ? `${country.phoneCode}${cleanedNumber}`
      : country.phoneCode;

    onChange?.(fullNumber);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchTerm('');
    setInputHint(null);
    updateFullNumber(country, phoneNumber);
  };

  const handlePhoneChange = (e) => {
    const { localDigits, hint } = sanitizeLocalPhoneInput(e.target.value, resolvedCountry);
    setPhoneNumber(localDigits);
    setInputHint(hint);
    updateFullNumber(resolvedCountry, localDigits);
  };

  const filteredCountries = countries.filter((country) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(search)
      || country.phoneCode.includes(search)
      || country.code.toLowerCase().includes(search)
    );
  });

  const placeholder = LOCAL_PLACEHOLDER[resolvedCountry.code]
    || 'Numéro local (sans indicatif)';

  const lockedLabel = prefixLocked
    ? `Indicatif ${resolvedCountry.phoneCode} — défini par le pays d'adresse`
    : undefined;

  return (
    <div className="country-phone-input">
      <div className="phone-input-wrapper">
        {prefixLocked ? (
          <div className="country-selector">
            <div
              className="country-selector-button country-selector-locked"
              aria-label={lockedLabel}
              title={lockedLabel}
            >
              <CountryFlag country={resolvedCountry} aria-hidden="true" />
              <span className="country-code">{resolvedCountry.phoneCode}</span>
            </div>
          </div>
        ) : (
          <div className="country-selector" ref={dropdownRef}>
            <button
              type="button"
              className={`country-selector-button ${isOpen ? 'open' : ''} ${errors.telephone ? 'error' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                  setIsOpen(!isOpen);
                }
              }}
              disabled={disabled}
            >
              <CountryFlag country={selectedCountry} aria-hidden="true" />
              <span className="country-code">{selectedCountry.phoneCode}</span>
              <span className="dropdown-arrow">▼</span>
            </button>

            {isOpen && (
              <div className="country-dropdown">
                <div className="country-search">
                  <input
                    type="text"
                    placeholder="Rechercher un pays..."
                    className="country-search-input"
                    value={searchTerm}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSearchTerm(e.target.value);
                    }}
                    autoFocus
                  />
                </div>
                <div className="country-list">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <div
                        key={country.code}
                        className={`country-option ${selectedCountry.code === country.code ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCountrySelect(country);
                        }}
                      >
                        <CountryFlag country={country} aria-hidden="true" />
                        <span className="country-name">{country.name}</span>
                        <span className="country-phone-code">{country.phoneCode}</span>
                      </div>
                    ))
                  ) : (
                    <div className="country-option no-results">
                      <span>Aucun pays trouvé</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <input
          type="tel"
          className={`phone-input ${errors.telephone ? 'error' : ''}`}
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          disabled={disabled}
          required
          autoComplete="tel-national"
          inputMode="numeric"
          id={id || 'phone-number-input'}
          aria-describedby={[ariaDescribedBy, inputHint ? `${id || 'phone'}-format-hint` : null].filter(Boolean).join(' ') || undefined}
          aria-invalid={ariaInvalid}
        />
      </div>

      {errors.telephone && (
        <div className="field-error">{errors.telephone}</div>
      )}

      {inputHint && !errors.telephone && (
        <small className="form-hint phone-input-format-hint" id={`${id || 'phone'}-format-hint`} role="status">
          {inputHint}
        </small>
      )}

      <small className="form-hint">
        Numéro complet : {resolvedCountry.phoneCode}{phoneNumber || '…'}
        {prefixLocked && ' — indicatif lié au pays choisi dans l\'adresse'}
      </small>
    </div>
  );
}

export default CountryPhoneInput;
